import { Proposal } from "@/models/proposal.model";
import { FormValues } from "@/models/proposalForm.model";
import { documentFormatter } from "@/utils/documentFormatter";
import {
  Box,
  Heading,
  Text,
  Button,
  Flex,
  VStack,
  Tag,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Grid,
  Input,
  Divider,
  useDisclosure,
} from "@chakra-ui/react";
import { useState, useRef, useEffect } from "react";
import { MdContentCopy } from "react-icons/md";
import { GoPerson } from "react-icons/go";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { titleCaseFormatter } from "@/utils/titleCaseFormatter";
import { copyImageToClipboard } from "@/utils/copyImageToClipboard";
import { useToast } from "@chakra-ui/react";
import { FiChevronDown } from "react-icons/fi";
import { InsuranceCardOverview } from "../../../InsuranceCardOverview";
import InstallmentModal from "./InstallmentModal";
import CopyImage from "./CopyImage";

interface ResultStepProps {
  data?: FormValues["resultStep"];
  proposal?: Proposal;
  updateData: (values: Partial<FormValues["resultStep"]>) => void;
  handleNext: () => void;
  handleBack: () => void;
}

export default function ResultStep({
  data,
  proposal,
  updateData,
  handleNext,
  handleBack,
}: ResultStepProps) {
  const initalAntecipatedYears = Math.min(
    data?.antecipatedYears ?? proposal?.anticipatedYears ?? 10,
    10
  );

  const [copyImageLoading, setCopyImageLoading] = useState(false);

  const [antecipatedYears, setAntecipatedYears] = useState<number>(
    initalAntecipatedYears
  );
  const [antecipatedYearsError, setAntecipatedYearsError] = useState<
    string | null
  >(null);

  const getIssueAmount = (years: number) => {
    return data?.chosenFinancialOption?.Installments
      ? data.chosenFinancialOption.Installments.slice(0, years + 1).reduce(
          (acc, installment) => acc + installment.payment,
          0
        )
      : undefined;
  };

  const getReleasedAmount = (years: number) => {
    const installments = data?.chosenFinancialOption?.Installments;
    return installments?.at(years)
      ? installments.at(years)?.releasedAmount
      : installments?.at(years - 1)?.releasedAmount;
  };

  const initialReleasedAmount =
    data?.releasedAmount ?? getReleasedAmount(initalAntecipatedYears);
  const initialIssueAmount =
    data?.chosenFinancialOption?.Installments?.at(initalAntecipatedYears + 1)
      ?.issueAmount ?? getIssueAmount(initalAntecipatedYears);

  const [releasedAmount, setReleasedAmount] = useState<number | undefined>(
    initialReleasedAmount
  );
  const [releasedAmountError, setReleasedAmountError] = useState<string | null>(
    null
  );
  const [issueAmount, setIssueAmount] = useState<number | undefined>(
    initialIssueAmount
  );

  const imageRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleCopyImage = async () => {
    try {
      const element = imageRef.current;
      if (!element) {
        toast({
          title: "Erro",
          description: "Elemento para copiar não foi encontrado.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      setCopyImageLoading(true);

      await copyImageToClipboard(element);

      toast({
        title: "Sucesso",
        description: "Imagem copiada para a área de transferência!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao copiar a imagem.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCopyImageLoading(false);
    }
  };

  const handleProductSelect = (value: string) => {
    const product = proposal?.optionsFinancialInfo?.find(
      (o) => o.Product?.name === value
    );
    updateData({ chosenFinancialOption: product });
  };

  const handleAntecipatedYearsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const parsedValue = parseInt(event.target.value, 10);

    if (event.target.value.startsWith("0")) return;

    validateAntecipatedYearsError(parsedValue);

    setAntecipatedYears(parsedValue);
    updateData({ antecipatedYears: parsedValue });
    updateIof(parsedValue);

    const newReleasedAmount = getReleasedAmount(parsedValue);
    if (newReleasedAmount !== undefined) {
      setReleasedAmount(newReleasedAmount);
      setIssueAmount(getIssueAmount(parsedValue));
      updateData({ releasedAmount: newReleasedAmount });
    }
  };

  const handleReleasedAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const parsedValue = parseFloat((Number(e.target.value.replace(/\D/g, "")) / 100).toFixed(2));
    if (isNaN(parsedValue)) return;

    setReleasedAmount(parsedValue);
    updateData({ releasedAmount: parsedValue });

    const installments = data?.chosenFinancialOption?.Installments ?? [];

    validateReleasedAmountError(parsedValue);

    const cumulativeAmounts = installments.map(({ releasedAmount }) => releasedAmount);

    const antecipatedYearsIndex = cumulativeAmounts.findIndex((_amount, index) => {
      if (index === 0) return false;
      return cumulativeAmounts[index] >= parsedValue;
    });

    const finalAntecipatedYears =
      antecipatedYearsIndex !== -1 ? antecipatedYearsIndex : 10;

    updateIof(finalAntecipatedYears);
    setAntecipatedYears(finalAntecipatedYears);
    setIssueAmount(getIssueAmount(finalAntecipatedYears));
    updateData({ antecipatedYears: finalAntecipatedYears });
  };

  const updateIof = (years: number) => {
    const iof = data?.chosenFinancialOption?.Installments
      ? data?.chosenFinancialOption?.Installments.slice(0, years + 1).reduce(
          (acc, installment) => acc + installment.financeTax,
          0
        )
      : undefined;

    if (iof !== undefined) {
      updateData({ iof });
    }
  };

  const validateReleasedAmountError = (amount?: number) => {
    const installments = data?.chosenFinancialOption?.Installments;
    if (!installments?.length || !amount) return;

    const minimalReleasedAmount = getReleasedAmount(1)!;
    const maximalReleasedAmount = getReleasedAmount(installments.length - 1)!;

    const formattedMinimalAmount = BrlCurrencyFormatter(minimalReleasedAmount);
    const formattedMaximalAmount = BrlCurrencyFormatter(maximalReleasedAmount);

    const errorConditions = [
      {
        condition: minimalReleasedAmount >= amount,
        message: `O Valor liberado deve ser maior que ${formattedMinimalAmount}`,
      },
      {
        condition: amount > maximalReleasedAmount,
        message: `O Valor liberado deve ser menor que ${formattedMaximalAmount}`,
      },
    ];

    const errorMessage =
      errorConditions.find(({ condition }) => condition)?.message || null;

    setReleasedAmountError(errorMessage);
  };

  const validateAntecipatedYearsError = (years: number) =>
    years < (proposal!.Processor!.investors?.at(0)?.minInstallments ?? 2) ||
    years > 10
      ? setAntecipatedYearsError(
          `O número de anos não pode ser menor que ${proposal!.Processor!.investors?.at(0)?.minInstallments ?? 2} ou maior que 10`
        )
      : setAntecipatedYearsError(null);

  useEffect(() => {
    updateIof(antecipatedYears);
    validateReleasedAmountError(releasedAmount);
    validateAntecipatedYearsError(antecipatedYears);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.chosenFinancialOption, antecipatedYears, releasedAmount]);

  return (
    <Flex
      direction={{ base: "column", lg: "row" }}
      justify="space-evenly"
      py="1rem"
      px={{ base: "1rem", lg: "2rem" }}
      wrap="wrap"
    >
      <Box
        w={{ base: "100%", lg: "35%" }}
        mb={{ base: 8, lg: 12 }}
        mt={{ base: 4, lg: 8 }}
      >
        <Heading
          size={{ base: "md", md: "lg" }}
          color="gray.500"
          fontFamily="Lexend Deca"
          fontWeight="300"
          mb={4}
          textAlign={{ base: "center", lg: "left" }}
        >
          Resultado da Simulação
        </Heading>
        <Tag
          mb={6}
          borderRadius="full"
          variant="solid"
          colorScheme="blue"
          backgroundColor="secondary"
          fontFamily="Lexend Deca"
          display="flex"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          width={{ base: "80%", sm: "40%", md: "40%", lg: "60%" }}
          fontSize={{ base: "sm", sm: "md", md: "lg" }}
        >
          <Text
            fontFamily="Lexend Deca"
            fontSize={{ base: "sm", md: "md", lg: "md" }}
          >
            Contrato averbado por{" "}
            <Text as="span" fontWeight="bold">
              {proposal?.Processor?.name}
            </Text>
          </Text>
        </Tag>

        <Box mb={6}>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color="gray.500"
            fontFamily="Lexend Deca"
            textAlign={{ base: "center", lg: "left" }}
          >
            Tabela Selecionada:
          </Text>

          <Flex justify={{ base: "center", lg: "flex-start" }} mt={2}>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<Box as={FiChevronDown} />}
                size="lg"
                fontWeight="bold"
                fontSize={{ base: "lg", md: "xl" }}
                color="secondary"
                fontFamily="Lexend Deca"
                bg="white"
                border="2px solid"
                borderColor={{ base: "gray.300", md: "transparent" }} // Thin border for mobile
                _focus={{ boxShadow: "none", borderColor: "outline" }}
                w={{ base: "100%", sm: "auto" }}
              >
                {data?.chosenFinancialOption?.Product?.name ||
                  "Select an option"}
              </MenuButton>
              <MenuList
                borderRadius="md"
                boxShadow="lg"
                py="2"
                maxH="200px"
                overflowY="auto"
              >
                {proposal?.optionsFinancialInfo?.map((opt) => (
                  <MenuItem
                    key={opt.Product?.name}
                    onClick={() => handleProductSelect(opt.Product?.name!)}
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="medium"
                    px="4"
                    py="2"
                  >
                    {titleCaseFormatter(opt.Product?.name!)}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
        </Box>

        <Text
          mt={8}
          fontSize={{ base: "md", md: "lg" }}
          color="gray.500"
          fontFamily="Lexend Deca"
          textAlign={{ base: "center", lg: "left" }}
        >
          Escolha o número de anos que seu cliente deseja:
        </Text>

        <HStack
          mt={4}
          spacing={4}
          alignItems="center"
          justify={{ base: "center", lg: "flex-start" }}
        >
          <Input
            value={antecipatedYears}
            onChange={handleAntecipatedYearsChange}
            fontSize={{ base: "2xl", md: "3xl" }}
            width={{ base: "6rem", md: "8rem" }}
            size="lg"
            fontWeight="bold"
            color="secondary"
            fontFamily="Lexend Deca"
            textAlign="center"
            type="number"
            min={1}
          />
          <Text
            fontSize={{ base: "md", md: "lg" }}
            fontFamily="Lexend Deca"
            color="gray.500"
          >
            anos
          </Text>
        </HStack>

        {antecipatedYearsError && (
          <Text
            color="red.500"
            mt={2}
            fontSize={{ base: "sm", md: "md" }}
            fontFamily="Lexend Deca"
            textAlign={{ base: "center", lg: "left" }}
          >
            {antecipatedYearsError}
          </Text>
        )}

        <Box mt={8} mb={8} position="relative">
          <Divider borderColor="gray.300" />
          <Text
            w="55%"
            position="absolute"
            top="-10px"
            left="50%"
            transform="translateX(-50%)"
            bg="white"
            px={2}
            fontSize={{ base: "sm", md: "md" }}
            color="gray.500"
            fontFamily="Lexend Deca"
          >
            Ou digite o valor desejado
          </Text>
        </Box>

        <HStack
          mt={4}
          spacing={4}
          alignItems="center"
          justify={{ base: "center", lg: "flex-start" }}
        >
          <Input
            value={BrlCurrencyFormatter(releasedAmount)}
            onChange={handleReleasedAmountChange}
            fontSize={{ base: "lg", md: "xl" }}
            width={{ base: "90%", md: "12rem", lg: "16rem" }}
            size="lg"
            fontWeight="bold"
            color="secondary"
            fontFamily="Lexend Deca"
            textAlign="center"
            type="text"
            placeholder="R$ 0,00"
          />

          <Text
            fontSize={{ base: "md", md: "lg" }}
            fontFamily="Lexend Deca"
            color="gray.500"
          >
            valor liberado
          </Text>
        </HStack>

        {releasedAmountError && (
          <Text
            color="red.500"
            mt={2}
            fontSize={{ base: "sm", md: "md" }}
            fontFamily="Lexend Deca"
            textAlign={{ base: "center", lg: "left" }}
          >
            {releasedAmountError}
          </Text>
        )}

        <Flex
          pt={{ base: "2rem", lg: "4rem" }}
          justifyContent="center"
          w="100%"
          gap={3}
          flexDirection={{ base: "column", sm: "row" }}
        >
          <Button
            colorScheme="secondary"
            variant="outline"
            w={{ base: "100%", sm: "35%" }}
            fontFamily="Lexend Deca"
            onClick={handleBack}
          >
            Voltar
          </Button>
          <Button
            bg="#51CD80"
            color="white"
            _hover={{ bg: "#48BB78" }}
            _active={{ bg: "#38A169" }}
            fontFamily="Lexend Deca"
            onClick={handleNext}
            isDisabled={!!antecipatedYearsError || !!releasedAmountError}
            w={{ base: "100%", sm: "35%" }}
          >
            Digitar Proposta
          </Button>
        </Flex>
      </Box>

      <VStack
        w={{ base: "100%", lg: "36%" }}
        spacing={4}
        pr={{ base: "0", lg: "5rem" }}
        mb={{ base: 8, lg: 0 }}
      >
        <Box
          bg="white"
          p={{ base: 4, md: 6 }}
          borderRadius="lg"
          boxShadow="md"
          borderColor="gray.200"
          position="relative"
          w="100%"
          minH="400px"
        >
          <Flex
            justify="space-between"
            align="center"
            mb={6}
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Flex align="center" ml={4} mb={{ base: 2, sm: 0 }}>
              <GoPerson size="3rem" color="#A0AEC0" />
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.500"
                ml={2}
                fontFamily="Lexend Deca"
              >
                {documentFormatter(proposal?.NaturalPerson?.document!) ??
                  "121.533.545-37"}
              </Text>
            </Flex>

            <Button
              colorScheme="blue"
              variant="ghost"
              display="flex"
              alignItems="center"
              px={2}
              py={1}
              onClick={handleCopyImage}
              isLoading={copyImageLoading}
              isDisabled={!!antecipatedYearsError || !!releasedAmountError}
              mt={{ base: 2, sm: 0 }}
            >
              <Text
                color="blue.600"
                fontFamily="Lexend Deca"
                fontSize="sm"
                mr={2}
              >
                Copiar Imagem
              </Text>
              <MdContentCopy size="1.25rem" color="#3182CE" />
            </Button>
          </Flex>

          <Box ml={{ base: 4, md: 6 }}>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="blue.600"
              fontFamily="Lexend Deca"
            >
              O valor liberado será de:
            </Text>
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              color="secondary"
              fontFamily="Lexend Deca"
            >
              {antecipatedYearsError || releasedAmountError
                ? "Valor não disponível"
                : BrlCurrencyFormatter(releasedAmount)}
            </Text>
          </Box>

          <VStack
            align="start"
            spacing={6}
            fontFamily="Lexend Deca"
            py="1rem"
            pr={{ base: "1rem", md: "3rem" }}
            ml={{ base: 4, md: 6 }}
          >
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={3}
              w="100%"
            >
              <Box>
                <Text fontSize="md" color="gray.500">
                  IOF
                </Text>
                <Text fontSize="md" fontWeight="bold" color="blue.600">
                  {BrlCurrencyFormatter(data?.iof!)}
                </Text>
              </Box>

              <Box>
                <Text fontSize="md" color="gray.500">
                  Valor Bloqueado
                </Text>
                <Text fontSize="md" fontWeight="bold" color="blue.600">
                  {BrlCurrencyFormatter(issueAmount!)}
                </Text>
              </Box>

              <Box>
                <Text fontSize="md" color="gray.500">
                  Taxa A.M.
                </Text>
                <Text fontSize="md" fontWeight="bold" color="blue.600">
                  {(data?.chosenFinancialOption?.monthlyRate! * 100)?.toFixed(
                    2
                  ) ?? "XXXXX"}
                  %
                </Text>
              </Box>

              <Box>
                <Text fontSize="md" color="gray.500">
                  Taxa A.A.
                </Text>
                <Text fontSize="md" fontWeight="bold" color="blue.600">
                  {(data?.chosenFinancialOption?.annualRate! * 100)?.toFixed(
                    2
                  ) ?? "XXXXX"}
                  %
                </Text>
              </Box>

              <Box>
                <Text fontSize="md" color="gray.500">
                  CET A.M.
                </Text>
                <Text fontSize="md" fontWeight="bold" color="blue.600">
                  {(proposal?.monthlyCET! * 100)?.toFixed(2) ?? "XXXXX"}%
                </Text>
              </Box>

              <Box>
                <Text fontSize="md" color="gray.500">
                  CET A.A.
                </Text>
                <Text fontSize="md" fontWeight="bold" color="blue.600">
                  {(proposal?.annualCET! * 100)?.toFixed(2) ?? "XXXXX"}%
                </Text>
              </Box>

              <Box gridColumn={{ base: "span 1", md: "2" }}>
                <Button
                  size="sm"
                  bg="#51CD80"
                  color="white"
                  _hover={{ bg: "#48BB78" }}
                  _active={{ bg: "#38A169" }}
                  fontFamily="Lexend Deca"
                  fontSize="md"
                  w="100%"
                  onClick={onOpen}
                  isDisabled={!!antecipatedYearsError || !!releasedAmountError}
                >
                  Ver Parcelas
                </Button>
              </Box>
            </Grid>
          </VStack>
        </Box>

        {data?.chosenFinancialOption?.tacInput ? null : (
          <InsuranceCardOverview />
        )}
      </VStack>

      <Box
        ref={imageRef}
        position="absolute"
        left="-9999px"
        top={0}
        width="400px"
        minH="400px"
        overflow="hidden"
      >
        <CopyImage
          releasedAmount={releasedAmount!}
          document={proposal?.NaturalPerson?.document!}
          antecipatedYears={antecipatedYears!}
          monthlyRate={data?.chosenFinancialOption?.monthlyRate!}
          issueAmount={issueAmount!}
        />
      </Box>

      <InstallmentModal
        isOpen={isOpen}
        onClose={onClose}
        installments={
          data?.chosenFinancialOption!.Installments!.slice(
            0,
            antecipatedYears ?? 10
          )!
        }
      />
    </Flex>
  );
}
